import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentPaymentMethodName, PaymentMethods, ProcessorName } from '@cargos/sprintpay-models';
import { AdvancedFlowApiService, AdyenComponentResponse, AdyenPaymentDataRequest } from '@cargos/sprintpay-ui';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { CustomerService, PaymentMethodsService } from 'src/app/shared/services';
import Swal from 'sweetalert2';
import { PaymentMethodsHandlerService } from '../../lib-cart/shared/services/payment-methods-handler.service';
import { PaymentMethodItemService } from '../services/payment-methods.service';

@Component({
    selector: 'app-credit-cards',
    templateUrl: './credit-cards.component.html',
    styleUrls: ['./credit-cards.component.scss'],
    standalone: false,
})
export class CreditCardsComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    private readonly customerService = inject(CustomerService);
    private readonly advancedFlowApiService = inject(AdvancedFlowApiService);
    public enableCreditCardForm: boolean;
    public loading: boolean;

    constructor(
        private paymentMethodItemService: PaymentMethodItemService,
        private paymentMethodsService: PaymentMethodsService,
        private router: Router,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.subscribeFormStatus();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeFormStatus(): void {
        this.paymentMethodItemService
            .getShowCardFormSelected$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (statusForm: boolean | null) => {
                    this.enableCreditCardForm = !!statusForm;
                },
            });
    }

    enableForm(): void {
        this.paymentMethodItemService.setShowCardForm(!this.enableCreditCardForm);
    }

    responseAdyen(responseAdyen: AdyenComponentResponse | null): void {
        if (!responseAdyen) {
            return;
        }
        if (responseAdyen?.type === 'completed') {
            this.loading = false;

            this.createNewCreditCard(responseAdyen);
        }
    }

    back(): void {
        this.router.navigate(['/cart']);
    }

    private createNewCreditCard(responseAdyen: AdyenComponentResponse): void {
        this.loading = true;
        const user = this.customerService.getCustomer();
        combineLatest({
            paymentMethodsGuest: this.paymentMethodsService.getGuestPaymentMethods$(),
            paymentMethods: this.paymentMethodsService.getPaymentMethodsRequest$(),
        })
            .pipe(take(1))
            .subscribe({
                next: ({ paymentMethodsGuest, paymentMethods }) => {
                    this.paymentMethodsHandlerService.setPaymentMethodSelected({
                        method: PaymentMethods.CREDIT_CARD,
                        token: responseAdyen.accountToken,
                    });
                    this.handleSaveCreditCardSuccess();
                    this.advancedFlowApiService.setPaymentMethod('Cards');
                    if (user?.isGuest) {
                        paymentMethodsGuest.forEach((method) => {
                            if (method.name === PaymentMethods.CREDIT_CARD && responseAdyen.card) {
                                method.items = [
                                    {
                                        accountingDetails: {
                                            ...responseAdyen.card.accountingDetails,
                                            cardType: responseAdyen.card.accountingDetails.cardType,
                                        },
                                        paymentToken: {
                                            token: responseAdyen.accountToken || null,
                                            paymentMethod: {
                                                name: ComponentPaymentMethodName.CREDIT_CARD,
                                                logTime: 10,
                                                componentName: 'credit-card',
                                            },
                                            paymentProcessor: { name: ProcessorName.Adyen },
                                        },
                                    },
                                ];
                            }
                        });
                        if (responseAdyen.adyenRegisterCardRequest) {
                            const adyenRegisterCardRequest: AdyenPaymentDataRequest = {
                                billAddress: responseAdyen.adyenRegisterCardRequest.BillAddress,
                                buyerIPV4Address: responseAdyen.adyenRegisterCardRequest.BuyerIPV4Address,
                                paymentVehicle: responseAdyen.adyenRegisterCardRequest.PaymentVehicle,
                                metadata: responseAdyen.adyenRegisterCardRequest.Metadata,
                            };
                            this.advancedFlowApiService.setAdyenRegisterCardRequest(adyenRegisterCardRequest);
                        }
                    }
                    this.back();
                },
                complete: () => {
                    this.loading = false;
                },
            });
    }

    private handleSaveCreditCardSuccess(lastFourDigit?: string): void {
        Swal.close();
        const message = lastFourDigit
            ? `Your credit card ending with ${lastFourDigit} was added successfully`
            : 'Your credit card was added successfully';

        Swal.fire({
            title: 'Credit Card added',
            width: '45em',
            imageUrl: 'assets/images/payment-methods/success.svg',
            text: message,
            showConfirmButton: false,
            showCancelButton: false,
            allowOutsideClick: false,
        });
        setTimeout(() => {
            Swal.close();
            const currentUrl = this.router.url;
            const previousUrl: string = currentUrl.includes('admin/cart') ? '/admin/cart' : '';
            this.back();
        }, 3000);
    }
}
